// ═══════════════════════════════════════════════════════════════════════════
// Per-action entity-creation translators (CC-2c sub-arm 1) — PACKAGE-INTERNAL
// ═══════════════════════════════════════════════════════════════════════════
//
// The thin per-action translators behind the ENTITY-CREATION domain
// (CREATE_TEXT/MTEXT/HATCH/DIMENSION/MLEADER/REVCLOUD). Each AI shape maps onto
// the corresponding contract `EditorCommand`, with no simplification — full
// required params per the EditorCommand + ТЗ.
//
// IMPORTANT (AI safe contour): these translators produce geometry
// `EditorCommand`s WITHOUT running the CC-2a fail-closed guard
// (`assertAiCallableEntityAction`). They are therefore PACKAGE-INTERNAL: this
// file is NOT re-exported by the barrel (`index.ts`). The ONLY public
// entity-creation entry is the guarded `translateEntityCommand` in
// `entityDispatcher.ts` (which imports these). This mirrors the BLOCK domain
// (per-action translators in non-barrelled `blockDispatcher*.ts`) and the
// DRAW-MODIFY domain (`drawModifyTranslators.ts`) — so a consumer of
// `@dxf-ai-editor/ai-commands` can never emit `geometry/create-text` etc. past
// the guard. Honours the CLAUDE.md rules:
//   "AI drafting … может только предлагать и исполнять команды через
//    безопасный контур." + "любые изменения геометрии только через Command
//    Engine."

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import {
  AI_COMMAND_SOURCE,
  pickSpaceId,
  type AiDispatchContext,
} from "./blockDispatcherShared";
import {
  AI_DIMENSION_KIND_TO_TYPE,
  type CreateDimensionAiCommand,
  type CreateHatchAiCommand,
  type CreateMtextAiCommand,
  type CreateMultileaderAiCommand,
  type CreateRevcloudAiCommand,
  type CreateTextAiCommand,
} from "./entitySchemas";

// ─── CREATE_TEXT ──────────────────────────────────────────────────
export function translateCreateText(
  command: CreateTextAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  return [
    {
      type: "geometry/create-text",
      source: AI_COMMAND_SOURCE,
      spaceId: pickSpaceId(command, context),
      insertionPoint: {
        x: command.insertionPoint.x,
        y: command.insertionPoint.y,
      },
      height: command.height,
      contents: command.contents,
      // DEGREES, verbatim — the create-text reducer stores rotation unchanged.
      rotation: command.rotation,
      styleName: command.styleName,
    },
  ];
}

// ─── CREATE_MTEXT ─────────────────────────────────────────────────
export function translateCreateMtext(
  command: CreateMtextAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  return [
    {
      type: "geometry/create-mtext",
      source: AI_COMMAND_SOURCE,
      spaceId: pickSpaceId(command, context),
      insertionPoint: {
        x: command.insertionPoint.x,
        y: command.insertionPoint.y,
      },
      nominalHeight: command.nominalHeight,
      referenceWidth: command.referenceWidth,
      contents: command.contents,
      styleName: command.styleName,
    },
  ];
}

// ─── CREATE_HATCH ─────────────────────────────────────────────────
//
// Either rectangular corners OR boundary paths must be present; a hatch with
// neither has no boundary and is rejected (full-fidelity — not a silent no-op).
export function translateCreateHatch(
  command: CreateHatchAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  const hasCorners =
    command.boundaryCorner1 !== undefined &&
    command.boundaryCorner2 !== undefined;
  const hasPaths =
    command.boundaryPaths !== undefined && command.boundaryPaths.length > 0;
  if (!hasCorners && !hasPaths) {
    throw new Error(
      "AI CREATE_HATCH: a hatch requires either boundaryCorner1 + boundaryCorner2 " +
        "(rectangular mode) or boundaryPaths (internal-pick mode); neither was provided.",
    );
  }

  const cmd: EditorCommand = {
    type: "geometry/create-hatch",
    source: AI_COMMAND_SOURCE,
    spaceId: pickSpaceId(command, context),
    patternName: command.patternName,
    patternScale: command.patternScale,
    patternAngle: command.patternAngle,
    ...(command.boundaryCorner1 !== undefined
      ? {
          boundaryCorner1: {
            x: command.boundaryCorner1.x,
            y: command.boundaryCorner1.y,
          },
        }
      : {}),
    ...(command.boundaryCorner2 !== undefined
      ? {
          boundaryCorner2: {
            x: command.boundaryCorner2.x,
            y: command.boundaryCorner2.y,
          },
        }
      : {}),
    ...(command.boundaryPaths !== undefined
      ? {
          boundaryPaths: command.boundaryPaths.map((path) => ({
            pathTypeFlag: path.pathTypeFlag,
            edges: path.edges.map((edge) =>
              edge.edgeType === "line"
                ? {
                    edgeType: "line" as const,
                    start: { x: edge.start.x, y: edge.start.y },
                    end: { x: edge.end.x, y: edge.end.y },
                  }
                : {
                    edgeType: "arc" as const,
                    center: { x: edge.center.x, y: edge.center.y },
                    radius: edge.radius,
                    startAngle: edge.startAngle,
                    endAngle: edge.endAngle,
                    counterclockwise: edge.counterclockwise,
                  },
            ),
          })),
        }
      : {}),
    ...(command.seedPoints !== undefined
      ? {
          seedPoints: command.seedPoints.map((p) => ({ x: p.x, y: p.y })),
        }
      : {}),
  };
  return [cmd];
}

// ─── CREATE_DIMENSION ─────────────────────────────────────────────
//
// The friendly `dimensionKind` enum maps to the numeric `dimensionType` (DXF
// group code 70) the contract + reducer use. `thirdPoint` is forwarded only
// when present (the 3-point angular vertex).
export function translateCreateDimension(
  command: CreateDimensionAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  const cmd: EditorCommand = {
    type: "geometry/create-dimension",
    source: AI_COMMAND_SOURCE,
    spaceId: pickSpaceId(command, context),
    extLine1Point: {
      x: command.extLine1Point.x,
      y: command.extLine1Point.y,
    },
    extLine2Point: {
      x: command.extLine2Point.x,
      y: command.extLine2Point.y,
    },
    dimensionLinePoint: {
      x: command.dimensionLinePoint.x,
      y: command.dimensionLinePoint.y,
    },
    dimensionType: AI_DIMENSION_KIND_TO_TYPE[command.dimensionKind],
    dimensionText: command.dimensionText,
    ...(command.thirdPoint !== undefined
      ? {
          thirdPoint: {
            x: command.thirdPoint.x,
            y: command.thirdPoint.y,
            z: command.thirdPoint.z,
          },
        }
      : {}),
  };
  return [cmd];
}

// ─── CREATE_MLEADER ───────────────────────────────────────────────
export function translateCreateMultileader(
  command: CreateMultileaderAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  return [
    {
      type: "geometry/create-multileader",
      source: AI_COMMAND_SOURCE,
      spaceId: pickSpaceId(command, context),
      arrowPoint: { x: command.arrowPoint.x, y: command.arrowPoint.y },
      landingPoint: { x: command.landingPoint.x, y: command.landingPoint.y },
      textContents: command.textContents,
    },
  ];
}

// ─── CREATE_REVCLOUD ──────────────────────────────────────────────
// REVCLOUD-MODES — the AI command carries the full creation-method
// surface (Rectangular / Polygonal / Freehand / Object + Style); the
// schema refine() already guaranteed mode/field coherence.
export function translateCreateRevcloud(
  command: CreateRevcloudAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  return [
    {
      type: "geometry/create-revcloud",
      source: AI_COMMAND_SOURCE,
      spaceId: pickSpaceId(command, context),
      creationMode: command.creationMode,
      ...(command.corner1
        ? { corner1: { x: command.corner1.x, y: command.corner1.y } }
        : {}),
      ...(command.corner2
        ? { corner2: { x: command.corner2.x, y: command.corner2.y } }
        : {}),
      ...(command.vertices
        ? { vertices: command.vertices.map((v) => ({ x: v.x, y: v.y })) }
        : {}),
      ...(command.sourceEntityId !== undefined
        ? { sourceEntityId: command.sourceEntityId }
        : {}),
      reverseDirection: command.reverseDirection,
      revisionCloudStyle: command.revisionCloudStyle,
      arcChordLength: command.arcChordLength,
    },
  ];
}
